import React from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(28, 28, 30, 0.95)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 8,
      padding: '8px 12px',
      fontSize: 13,
    }}>
      <div style={{ color: '#fff', fontWeight: 600 }}>{label}</div>
      <div style={{ color: payload[0].color, marginTop: 2 }}>
        {payload[0].value.toLocaleString()}
      </div>
    </div>
  );
};

const BarChartTemplate = ({ params, subject }) => {
  const data = params?.data || [];
  const xLabel = params?.x_label || '';
  const yLabel = params?.y_label || '';

  if (data.length === 0) {
    return <p style={{ color: 'var(--panel-text-secondary)' }}>No chart data available.</p>;
  }

  // Get accent color from subject — mirrors CSS tokens
  const SUBJECT_COLORS = {
    history: '#D4956A',
    science: '#30D158',
    biology: '#30D158',
    ela: '#BF5AF2',
    chemistry: '#64D2FF',
    math: '#FF9F0A',
    geography: '#30D158',
    social_studies: '#D4956A',
  };
  const accent = SUBJECT_COLORS[subject] || '#6DB4CD';

  return (
    <motion.div
      className="bar-chart-container"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 20, left: 8, bottom: 4 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(255,255,255,0.06)"
            horizontal={false}
          />
          <XAxis
            type="number"
            tick={{ fill: '#a1a1a6', fontSize: 12 }}
            axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            tickLine={false}
            label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -2, fill: '#a1a1a6', fontSize: 12 } : undefined}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: '#a1a1a6', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={100}
            label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fill: '#a1a1a6', fontSize: 12 } : undefined}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={32}>
            {data.map((_, index) => (
              <Cell key={index} fill={accent} fillOpacity={0.8 - index * 0.04} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </motion.div>
  );
};

export default BarChartTemplate;
